import React, { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import useCoopMemberRole from '@/hooks/useCoopMemberRole';
import { ToastContext } from '@/components/ToastProvider.jsx';
import { Plus, Edit, Trash2, Eye, EyeOff, Calendar, Tag, Percent, DollarSign, RefreshCw } from 'lucide-react';
import { initializeDiscounts, checkDiscountsExist } from '@/lib/initDiscounts';

export default function DiscountManagement() {
  const { isAdmin } = useCoopMemberRole();
  const { notify } = React.useContext(ToastContext);
  
  const [vouchers, setVouchers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [activeTab, setActiveTab] = useState('vouchers');
  const [discountsExist, setDiscountsExist] = useState({ vouchersExist: false, couponsExist: false });
  const [initializing, setInitializing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    description: '',
    minOrderAmount: '',
    maxDiscount: '',
    maxUsagePerUser: 1, // How many times each user can use this discount
    totalUsageLimit: null, // Maximum total uses across all users (optional)
    enabled: true,
    validFrom: '',
    validUntil: '',
    category: '',
    restrictions: {
      userGroups: ['all'],
      excludedProducts: [],
      excludedCategories: [],
      paymentMethods: ['all']
    }
  });

  useEffect(() => {
    if (!isAdmin) return;

    setLoading(true);
    
    // Listen to vouchers
    const vouchersUnsub = onSnapshot(
      query(collection(db, 'vouchers'), orderBy('createdAt', 'desc')),
      (snap) => setVouchers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Listen to coupons
    const couponsUnsub = onSnapshot(
      query(collection(db, 'coupons'), orderBy('createdAt', 'desc')),
      (snap) => setCoupons(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Check if discounts exist
    checkDiscountsExist().then(setDiscountsExist);

    setLoading(false);

    return () => {
      vouchersUnsub();
      couponsUnsub();
    };
  }, [isAdmin]);

  // Auto-generate discount code
  const generateDiscountCode = () => {
    const adjectives = ['SAVE', 'DEAL', 'OFFER', 'SPECIAL', 'FLASH', 'SUMMER', 'WINTER', 'SPRING', 'FALL', 'HOLIDAY', 'WEEKEND', 'DAILY', 'MONTHLY', 'QUICK', 'FAST', 'BEST', 'TOP', 'PREMIUM', 'EXCLUSIVE', 'LIMITED'];
    const nouns = ['SALE', 'DISCOUNT', 'COUPON', 'VOUCHER', 'CODE', 'DEAL', 'OFFER', 'PRICE', 'RATE', 'VALUE', 'BONUS', 'GIFT', 'REWARD', 'BENEFIT', 'ADVANTAGE', 'OPPORTUNITY', 'CHANCE', 'LUCK', 'FORTUNE', 'SUCCESS'];
    const numbers = Math.floor(Math.random() * 900) + 100; // 100-999
    
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    
    const generatedCode = `${adjective}${noun}${numbers}`;
    setFormData({ ...formData, code: generatedCode });
  };

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      value: '',
      description: '',
      minOrderAmount: '',
      maxDiscount: '',
      maxUsagePerUser: 1,
      totalUsageLimit: null,
      enabled: true,
      validFrom: '',
      validUntil: '',
      category: '',
      restrictions: {
        userGroups: ['all'],
        excludedProducts: [],
        excludedCategories: [],
        paymentMethods: ['all']
      }
    });
    setEditingDiscount(null);
  };

  const handleInitializeDiscounts = async () => {
    setInitializing(true);
    try {
      const result = await initializeDiscounts();
      if (result.success) {
        notify('Sample discounts initialized successfully!', { type: 'success' });
        // Refresh the discounts exist check
        const exists = await checkDiscountsExist();
        setDiscountsExist(exists);
      } else {
        notify(`Failed to initialize discounts: ${result.error}`, { type: 'error' });
      }
    } catch (error) {
              // Error initializing discounts
      notify('Error initializing discounts', { type: 'error' });
    } finally {
      setInitializing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
             const discountData = {
         ...formData,
         value: Number(formData.value),
         minOrderAmount: Number(formData.minOrderAmount),
         maxDiscount: Number(formData.maxDiscount),
         usedCount: editingDiscount ? editingDiscount.usedCount : 0,
         validFrom: new Date(formData.validFrom),
         validUntil: new Date(formData.validUntil),
         createdAt: editingDiscount ? editingDiscount.createdAt : new Date(),
         updatedAt: new Date(),
         createdBy: 'admin',
         usageHistory: editingDiscount ? editingDiscount.usageHistory : []
       };

      if (editingDiscount) {
        // Update existing discount
        await updateDoc(doc(db, activeTab, editingDiscount.id), discountData);
        notify('Discount updated successfully!', { type: 'success' });
      } else {
        // Create new discount
        await addDoc(collection(db, activeTab), discountData);
        notify('Discount created successfully!', { type: 'success' });
      }

      setShowCreateModal(false);
      resetForm();
    } catch (error) {
              // Error saving discount
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const toggleDiscountStatus = async (discountId, currentStatus) => {
    try {
      await updateDoc(doc(db, activeTab, discountId), { enabled: !currentStatus });
      notify(`Discount ${!currentStatus ? 'enabled' : 'disabled'} successfully!`, { type: 'success' });
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!confirm('Are you sure you want to delete this discount? This action cannot be undone.')) return;
    
    try {
      await deleteDoc(doc(db, activeTab, discountId));
      notify('Discount deleted successfully!', { type: 'success' });
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const editDiscount = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      code: discount.code,
      type: discount.type,
      value: discount.value.toString(),
      description: discount.description,
      minOrderAmount: discount.minOrderAmount.toString(),
      maxDiscount: discount.maxDiscount.toString(),
      maxUsagePerUser: discount.maxUsagePerUser || 1,
      totalUsageLimit: discount.totalUsageLimit || null,
      enabled: discount.enabled,
      validFrom: discount.validFrom.toDate().toISOString().split('T')[0],
      validUntil: discount.validUntil.toDate().toISOString().split('T')[0],
      category: discount.category || '',
      restrictions: discount.restrictions || {
        userGroups: ['all'],
        excludedProducts: [],
        paymentMethods: ['all']
      }
    });
    setShowCreateModal(true);
  };

  if (!isAdmin) return <div className="card p-4">You do not have access.</div>;

  if (loading) return <div className="card p-4">Loading...</div>;

  const currentDiscounts = activeTab === 'vouchers' ? vouchers : coupons;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Discount Management</h2>
          <p className="text-gray-600 mt-1">Manage vouchers, coupons, and discount settings</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          Create Discount
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('vouchers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'vouchers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🎫 Vouchers ({vouchers.length})
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'coupons'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🏷️ Coupons ({coupons.length})
          </button>
        </nav>
      </div>

      {/* Initialize Sample Discounts */}
      {(!discountsExist.vouchersExist || !discountsExist.couponsExist) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0">⚠️</div>
            <div className="flex-1">
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-2">Sample discounts not found</p>
                <p className="mb-3">Click the button below to create sample voucher and coupon documents for testing.</p>
                <button
                  onClick={handleInitializeDiscounts}
                  disabled={initializing}
                  className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {initializing ? (
                    <>
                      <RefreshCw className="w-4 h-4 inline mr-2 animate-spin" />
                      Initializing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 inline mr-2" />
                      Initialize Sample Discounts
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discounts List */}
      <div className="space-y-4">
        {currentDiscounts.map((discount) => (
          <div key={discount.id} className="card p-4 border-l-4 border-blue-500">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{discount.code}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    discount.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {discount.enabled ? 'Active' : 'Disabled'}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    discount.type === 'percentage' ? 'bg-blue-100 text-blue-800' :
                    discount.type === 'fixed' ? 'bg-purple-100 text-purple-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {discount.type === 'percentage' ? `${discount.value}%` :
                     discount.type === 'fixed' ? `₱${discount.value}` : 'Free Shipping'}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-2">{discount.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-500">
                  <div>
                    <span className="font-medium">Min Order:</span> ₱{discount.minOrderAmount}
                  </div>
                  <div>
                    <span className="font-medium">Max Discount:</span> 
                    {discount.type === 'percentage' ? ` ₱${discount.maxDiscount}` : ` ₱${discount.maxDiscount}`}
                  </div>
                  <div>
                    <span className="font-medium">Valid:</span> {discount.validFrom.toDate().toLocaleDateString()} - {discount.validUntil.toDate().toLocaleDateString()}
                  </div>
                </div>
                
                {/* Usage Limits Display */}
                {discount.maxUsagePerUser && (
                  <div className="text-xs text-purple-600 mt-2">
                    Usage: {discount.maxUsagePerUser} per user
                    {discount.totalUsageLimit && ` • Total: ${discount.totalUsageLimit}`}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => toggleDiscountStatus(discount.id, discount.enabled)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title={discount.enabled ? 'Disable' : 'Enable'}
                >
                  {discount.enabled ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => editDiscount(discount)}
                  className="p-2 text-blue-400 hover:text-blue-600"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteDiscount(discount.id)}
                  className="p-2 text-red-400 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {currentDiscounts.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No {activeTab} found. Create your first one!
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingDiscount ? 'Edit Discount' : 'Create New Discount'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={generateDiscountCode}
                        className="px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-sm font-medium transition-colors"
                        title="Generate a random discount code"
                      >
                        Generate
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter a custom code or click Generate for a random one
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="free_shipping">Free Shipping</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                    <input
                      type="number"
                      value={formData.value}
                      onChange={(e) => setFormData({...formData, value: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      step="0.01"
                      min="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount</label>
                    <input
                      type="number"
                      value={formData.maxDiscount}
                      onChange={(e) => setFormData({...formData, maxDiscount: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount</label>
                  <input
                    type="number"
                    value={formData.minOrderAmount}
                    onChange={(e) => setFormData({...formData, minOrderAmount: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    step="0.01"
                    min="0"
                  />
                </div>

                {/* Usage Limits Section */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Usage Limits</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Usage Per User *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        required
                        value={formData.maxUsagePerUser}
                        onChange={(e) => setFormData({ ...formData, maxUsagePerUser: Number(e.target.value) })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How many times can each user use this discount? (e.g., 1 for one-time use, 5 for multiple uses)
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Usage Limit (Optional)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.totalUsageLimit || ''}
                        onChange={(e) => setFormData({ ...formData, totalUsageLimit: e.target.value ? Number(e.target.value) : null })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Unlimited"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum total uses across all users (leave empty for unlimited)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {activeTab === 'coupons' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., seasonal, new_user"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                    <input
                      type="date"
                      value={formData.validFrom}
                      onChange={(e) => setFormData({...formData, validFrom: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                    <input
                      type="date"
                      value={formData.validUntil}
                      onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enabled" className="ml-2 block text-sm text-gray-900">
                    Enabled
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {editingDiscount ? 'Update Discount' : 'Create Discount'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
